import { create } from "zustand";

import {
  PaginatedTranscriptionResult,
  transcriptionApi,
  TranscriptionListItem,
  TranscriptionResult
} from "../api/transcription.api";
import { TranscriptionStatus } from "../types/transcription";

// TTL кеша - 30 секунд
const CACHE_TTL = 30 * 1000;

interface CachedData {
  data: PaginatedTranscriptionResult;
  timestamp: number;
  speakerNames: Record<string, Record<number, string>>;
}

// Результат догрузки детали: "skipped" (оптимистичная запись)
// нужно отличать от "error", иначе карточка покажет ложную ошибку
type DetailLoadResult = "loaded" | "skipped" | "error";

interface HistoryState {
  // Кеш по ключу "page:limit:search"
  cache: Map<string, CachedData>;

  // Флаги загрузки
  isInitialLoading: boolean; // Первая загрузка без кеша
  isRefreshing: boolean; // Фоновое обновление при наличии кеша

  error: string | null;

  wsResyncSignal: number;

  // Ожидание апгрейда превью → полная версия. Живёт в сторе (не в локальном
  // useState карточки), чтобы переживать размонтирование TranscriptionCard
  // при переходах между страницами SPA.
  upgradingPreviews: Record<string, { waitingTranscriptionId: string }>;

  // Превью, ради которого пользователь только что вернулся с оплаты: минуты
  // может ещё не зачислить вебхук, и до их появления показывать CTA «оплатить»
  // нельзя — иначе человек платит второй раз
  awaitingPaymentPreviewId: string | null;

  // Presigned URL аудио, предзагруженные параллельно с деталями карточки:
  // без этого плеер ждал загрузки деталей и запрашивал URL только после
  // монтирования — аудио появлялось заметно позже текста
  // url === null — запрос был и не удался: нужно отличать от «ещё не запрашивали»,
  // иначе карточка ждала бы аудио вечно
  audioUrls: Record<string, { url: string | null; fetchedAt: number }>;

  // Actions
  fetchHistory: (userId: string | undefined, page: number, limit: number, search: string) => Promise<void>;
  loadTranscriptionDetail: (transcriptionId: string) => Promise<DetailLoadResult>;
  prefetchAudioUrl: (transcriptionId: string) => Promise<void>;
  getCachedAudioUrl: (transcriptionId: string) => string | null;
  /** Запрос presigned URL завершился — успехом или ошибкой */
  isAudioSettled: (transcriptionId: string) => boolean;
  getCachedData: (page: number, limit: number, search: string) => CachedData | null;
  isCacheValid: (page: number, limit: number, search: string) => boolean;
  updateTranscriptionInCache: (
    transcriptionId: string,
    updatedTranscription: Partial<TranscriptionResult> & { id: string }
  ) => void;
  updateSpeakerNameInCache: (transcriptionId: string, speakerNumber: number, name: string) => void;
  removeTranscriptionFromCache: (transcriptionId: string) => void;
  addOptimisticTranscription: (
    fileIdOrUrl: number | string,
    fileName: string,
    estimatedMinutes?: number,
    isPreview?: boolean,
    language?: string
  ) => void;
  signalWsResync: () => void;
  clearCache: () => void;
  setUpgradeWaiting: (previewId: string, transcriptionId: string) => void;
  clearUpgradeWaiting: (previewId: string) => void;
  setAwaitingPaymentPreview: (previewId: string | null) => void;
}

// Генерация ключа кеша
const getCacheKey = (page: number, limit: number, search: string): string => {
  return `${page}:${limit}:${search}`;
};

const fetchHistoryInflight = new Map<string, number>();
let fetchHistoryRequestId = 0;

// Защита от параллельных догрузок одной и той же транскрипции
const detailInflight = new Map<string, Promise<DetailLoadResult>>();

// Защита от параллельных запросов presigned URL одной и той же записи
const audioInflight = new Set<string>();

// Presigned URL живёт дольше, но перестраховываемся: после этого срока
// запрашиваем свежий, чтобы плеер не упёрся в протухшую ссылку
const AUDIO_URL_TTL = 30 * 60 * 1000;

/** Максимум ожидания метаданных аудио при прогреве — дальше не держим карточку */
const AUDIO_WARMUP_TIMEOUT = 15000;

/**
 * Оптимистичная запись — заглушка на первые секунды, пока сервер не отдаст
 * реальную. Долгая транскрипция здесь ни при чём: реальная запись создаётся
 * сразу со статусом queued, и дальше «Обработку» показывает уже она. Если
 * заглушка не сматчилась за этот срок — это сирота, и держать её нельзя,
 * иначе карточка вечно висит в «Обработке» с нулевой длительностью.
 */
const OPTIMISTIC_MAX_AGE = 3 * 60 * 1000;

/**
 * Элементы держим в модульной карте, иначе сборщик мусора может прервать
 * начатую загрузку. Чистятся вместе с кешем истории.
 */
const audioWarmers = new Map<string, HTMLAudioElement>();

/**
 * Прогрев аудио: получить presigned URL мало — браузеру ещё нужно скачать
 * метаданные, иначе плеер открывается с нулевой длительностью. Ждём
 * loadedmetadata (или ошибку/таймаут) и только потом считаем шаг завершённым.
 */
const warmUpAudio = (transcriptionId: string, url: string): Promise<void> => {
  if (typeof window === "undefined" || typeof Audio === "undefined") {
    return Promise.resolve();
  }

  return new Promise<void>(resolve => {
    const audio = new Audio();
    // Именно metadata, а не auto: auto тянет весь файл, а запись на час — это
    // сотни мегабайт в фоне на каждую прогреваемую карточку. Для длительности
    // и готовности плеера достаточно заголовка
    audio.preload = "metadata";
    audioWarmers.set(transcriptionId, audio);

    let timer = 0;
    const finish = (): void => {
      window.clearTimeout(timer);
      audio.removeEventListener("loadedmetadata", finish);
      audio.removeEventListener("error", finish);
      // Обрываем незавершённую загрузку: без этого элемент продолжает качать
      // файл и после того, как метаданные уже получены. Именно removeAttribute:
      // пустой src в WebKit резолвится относительно адреса документа, и элемент
      // уходит качать саму страницу
      audio.removeAttribute("src");
      audio.load();
      audioWarmers.delete(transcriptionId);
      resolve();
    };

    audio.addEventListener("loadedmetadata", finish, { once: true });
    audio.addEventListener("error", finish, { once: true });
    timer = window.setTimeout(finish, AUDIO_WARMUP_TIMEOUT);

    audio.src = url;
    audio.load();
  });
};

export const useHistoryStore = create<HistoryState>((set, get) => ({
  cache: new Map(),
  isInitialLoading: false,
  isRefreshing: false,
  error: null,
  wsResyncSignal: 0,
  upgradingPreviews: {},
  awaitingPaymentPreviewId: null,
  audioUrls: {},

  fetchHistory: async (userId, page, limit, search): Promise<void> => {
    if (!userId) {
      return;
    }

    const cacheKey = getCacheKey(page, limit, search);
    const state = get();
    const hasCachedData = state.cache.has(cacheKey);
    const requestId = ++fetchHistoryRequestId;

    try {
      // Если есть кеш - показываем isRefreshing, если нет - isInitialLoading
      if (hasCachedData) {
        set({ isRefreshing: true, error: null });
      } else {
        set({ isInitialLoading: true, error: null });
      }

      fetchHistoryInflight.set(cacheKey, requestId);

      const data = await transcriptionApi.getUserHistory({
        page,
        limit,
        search: search || undefined
      });

      if (fetchHistoryInflight.get(cacheKey) !== requestId) {
        return;
      }
      fetchHistoryInflight.delete(cacheKey);

      const freshCache = get().cache;
      const newCache = new Map(freshCache);

      // Список приходит облегчённым (без segments/utterances/aiSummary и с обрезанным text),
      // поэтому уже догруженные детали переносим из кеша, иначе фоновый рефреш их затирает
      const loadedDetails = new Map<string, TranscriptionResult>();
      freshCache.forEach(cached => {
        cached.data.items.forEach(t => {
          if (t.isDetailLoaded) {
            loadedDetails.set(t.id, t);
          }
        });
      });

      const mergeWithLoadedDetail = (item: TranscriptionListItem): TranscriptionResult => {
        const loaded = loadedDetails.get(item.id);
        // Смена статуса/типа означает, что деталь устарела — пусть перезапросится
        if (!loaded || loaded.status !== item.status || loaded.isPreview !== item.isPreview) {
          return item;
        }
        return { ...loaded, ...item, text: loaded.text, isDetailLoaded: true };
      };

      // Имена спикеров в лёгкий список не приходят — переносим их из кеша,
      // но только для транскрипций текущей страницы, иначе карта растёт бесконечно
      const cachedSpeakerNames = freshCache.get(cacheKey)?.speakerNames || {};
      const speakerNames: Record<string, Record<number, string>> = {};
      if (data.items && Array.isArray(data.items)) {
        data.items.forEach(t => {
          const names = cachedSpeakerNames[t.id] || loadedDetails.get(t.id)?.speakerNames;
          if (names) {
            speakerNames[t.id] = names;
          }
        });
      }

      let items = (data.items || []).map(mergeWithLoadedDetail);
      if (page === 1) {
        const cached = freshCache.get(cacheKey);
        if (cached) {
          const optimistic = cached.data.items.filter(t => {
            if (!t.id.startsWith("optimistic-")) {
              return false;
            }
            const isUrl = t.id.startsWith("optimistic-url-");
            // Для файлов основной ключ — fileId, но если сервер отдал запись
            // без него, совпадение по имени тоже снимает заглушку
            const matched = items.some(real =>
              isUrl ? real.fileName === t.fileName : real.fileId === t.fileId || real.fileName === t.fileName
            );
            if (matched) {
              return false;
            }
            return Date.now() - new Date(t.createdAt).getTime() < OPTIMISTIC_MAX_AGE;
          });
          if (optimistic.length > 0) {
            items = [...optimistic, ...items.filter(t => !t.id.startsWith("optimistic-"))];
          }
        }
      }

      // Обновляем кеш
      newCache.set(cacheKey, {
        data: { ...data, items },
        timestamp: Date.now(),
        speakerNames
      });

      set({
        cache: newCache,
        isInitialLoading: false,
        isRefreshing: false,
        error: null
      });
    } catch (err) {
      if (fetchHistoryInflight.get(cacheKey) !== requestId) {
        return;
      }
      fetchHistoryInflight.delete(cacheKey);
      console.error("Failed to fetch history:", err);
      set({
        error: "Не удалось загрузить историю транскрибаций",
        isInitialLoading: false,
        isRefreshing: false
      });
    }
  },

  // Догрузка тяжёлых полей (segments/utterances/aiSummary) при раскрытии карточки:
  // список приходит облегчённым, детали тянем по требованию
  loadTranscriptionDetail: (transcriptionId): Promise<DetailLoadResult> => {
    if (transcriptionId.startsWith("optimistic-")) {
      return Promise.resolve("skipped");
    }

    const inflight = detailInflight.get(transcriptionId);
    if (inflight) {
      return inflight;
    }

    const request = (async (): Promise<DetailLoadResult> => {
      try {
        const detail = await transcriptionApi.getResult(transcriptionId);
        get().updateTranscriptionInCache(transcriptionId, { ...detail, isDetailLoaded: true });

        if (detail.speakerNames) {
          const state = get();
          const newCache = new Map(state.cache);
          newCache.forEach((cachedData, key) => {
            if (cachedData.data.items.some(t => t.id === transcriptionId)) {
              newCache.set(key, {
                ...cachedData,
                speakerNames: {
                  ...cachedData.speakerNames,
                  [transcriptionId]: detail.speakerNames as Record<number, string>
                }
              });
            }
          });
          set({ cache: newCache });
        }

        return "loaded";
      } catch (err) {
        console.error("Failed to load transcription detail:", err);
        return "error";
      } finally {
        detailInflight.delete(transcriptionId);
      }
    })();

    detailInflight.set(transcriptionId, request);
    return request;
  },

  // Предзагрузка аудио параллельно с деталями: карточка запускает оба запроса
  // сразу при раскрытии, поэтому плеер готов одновременно с текстом
  prefetchAudioUrl: async (transcriptionId): Promise<void> => {
    if (transcriptionId.startsWith("optimistic-") || audioInflight.has(transcriptionId)) {
      return;
    }

    if (get().isAudioSettled(transcriptionId)) {
      return;
    }

    audioInflight.add(transcriptionId);

    try {
      const { url } = await transcriptionApi.getAudioUrl(transcriptionId);
      // Ждём именно метаданные, а не только ссылку: иначе карточка откроется
      // с плеером на 0:00, который потом дёрнется на реальную длительность
      await warmUpAudio(transcriptionId, url);
      set(state => ({
        audioUrls: { ...state.audioUrls, [transcriptionId]: { url, fetchedAt: Date.now() } }
      }));
    } catch {
      // Аудио могло истечь или его нет вовсе. Фиксируем неудачу, чтобы карточка
      // не ждала его бесконечно; плеер сам покажет корректную причину
      set(state => ({
        audioUrls: { ...state.audioUrls, [transcriptionId]: { url: null, fetchedAt: Date.now() } }
      }));
    } finally {
      audioInflight.delete(transcriptionId);
    }
  },

  getCachedAudioUrl: (transcriptionId): string | null => {
    const cached = get().audioUrls[transcriptionId];
    if (!cached || Date.now() - cached.fetchedAt > AUDIO_URL_TTL) {
      return null;
    }
    return cached.url;
  },

  isAudioSettled: (transcriptionId): boolean => {
    const cached = get().audioUrls[transcriptionId];
    return Boolean(cached) && Date.now() - cached.fetchedAt <= AUDIO_URL_TTL;
  },

  getCachedData: (page, limit, search): CachedData | null => {
    const cacheKey = getCacheKey(page, limit, search);
    return get().cache.get(cacheKey) || null;
  },

  isCacheValid: (page, limit, search): boolean => {
    const cached = get().getCachedData(page, limit, search);
    if (!cached) {
      return false;
    }
    const now = Date.now();
    return now - cached.timestamp < CACHE_TTL;
  },

  updateTranscriptionInCache: (transcriptionId, updatedTranscription): void => {
    const state = get();
    const newCache = new Map(state.cache);

    // Обновляем транскрипцию во всех кешах где она есть
    newCache.forEach((cachedData, key) => {
      // Сначала проверяем, есть ли оптимистичная запись
      let optimisticIndex = -1;

      // Для файлов: ищем по fileId, при промахе — по имени файла,
      // иначе заглушка останется в списке и перекроет реальную запись
      if (updatedTranscription.fileId) {
        optimisticIndex = cachedData.data.items.findIndex(
          t => t.id.startsWith("optimistic-") && t.fileId === updatedTranscription.fileId
        );

        if (optimisticIndex === -1 && updatedTranscription.fileName) {
          optimisticIndex = cachedData.data.items.findIndex(
            t => t.id.startsWith("optimistic-") && t.fileName === updatedTranscription.fileName
          );
        }
      }
      // Для URL: ищем по fileName (fileId = null)
      else if (updatedTranscription.fileName) {
        optimisticIndex = cachedData.data.items.findIndex(
          t => t.id.startsWith("optimistic-url-") && t.fileName === updatedTranscription.fileName
        );
      }

      let updatedItems: TranscriptionResult[];

      if (optimisticIndex !== -1) {
        // Заменяем оптимистичную запись на реальную, мержим с существующими данными
        updatedItems = [...cachedData.data.items];
        updatedItems[optimisticIndex] = {
          ...updatedItems[optimisticIndex],
          ...updatedTranscription
        };
      } else {
        // Если оптимистичной записи нет, просто обновляем существующую по ID, мержим с существующими данными
        updatedItems = cachedData.data.items.map(t =>
          t.id === transcriptionId ? { ...t, ...updatedTranscription } : t
        );
      }

      newCache.set(key, {
        ...cachedData,
        data: {
          ...cachedData.data,
          items: updatedItems
        }
      });
    });

    set({ cache: newCache });
  },

  updateSpeakerNameInCache: (transcriptionId, speakerNumber, name): void => {
    const state = get();
    const newCache = new Map(state.cache);

    // Обновляем speaker name во всех кешах где есть эта транскрипция
    newCache.forEach((cachedData, key) => {
      const hasTranscription = cachedData.data.items.some(t => t.id === transcriptionId);

      if (hasTranscription) {
        newCache.set(key, {
          ...cachedData,
          speakerNames: {
            ...cachedData.speakerNames,
            [transcriptionId]: {
              ...cachedData.speakerNames[transcriptionId],
              [speakerNumber]: name
            }
          }
        });
      }
    });

    set({ cache: newCache });
  },

  removeTranscriptionFromCache: (transcriptionId): void => {
    audioWarmers.delete(transcriptionId);
    const state = get();
    const newCache = new Map(state.cache);

    // Удаляем транскрипцию из всех кешей и обновляем счетчики
    newCache.forEach((cachedData, key) => {
      const filteredItems = cachedData.data.items.filter(t => t.id !== transcriptionId);

      // Если транскрипция была удалена (длина массива изменилась)
      if (filteredItems.length !== cachedData.data.items.length) {
        const newTotal = Math.max(0, cachedData.data.total - 1);
        const newTotalPages = Math.max(1, Math.ceil(newTotal / cachedData.data.items.length || 1));

        newCache.set(key, {
          ...cachedData,
          data: {
            ...cachedData.data,
            items: filteredItems,
            total: newTotal,
            totalPages: newTotalPages
          },
          // Удаляем speaker names для этой транскрипции
          speakerNames: Object.fromEntries(
            Object.entries(cachedData.speakerNames).filter(([id]) => id !== transcriptionId)
          )
        });
      }
    });

    const restUpgrading = { ...state.upgradingPreviews };
    delete restUpgrading[transcriptionId];

    set({ cache: newCache, upgradingPreviews: restUpgrading });
  },

  addOptimisticTranscription: (fileIdOrUrl, fileName, estimatedMinutes, isPreview = false, language = "ru"): void => {
    const state = get();
    const newCache = new Map(state.cache);

    // Если кеша нет - не добавляем оптимистичную запись
    if (newCache.size === 0) {
      return;
    }

    // Определяем тип: fileId (число) или URL (строка)
    const isUrl = typeof fileIdOrUrl === "string";
    const optimisticId = isUrl ? `optimistic-url-${Date.now()}` : `optimistic-${fileIdOrUrl}`;

    // Создаем оптимистичную запись
    const optimisticTranscription: TranscriptionResult = {
      id: optimisticId,
      fileId: isUrl ? null : fileIdOrUrl,
      fileName,
      duration: estimatedMinutes || 0,
      status: TranscriptionStatus.PROCESSING,
      // Бэкенд при "auto" сохраняет ru — пишем то же, иначе карточка перерисуется
      language: language === "auto" ? "ru" : language,
      createdAt: new Date().toISOString(),
      text: "",
      utterances: [],
      isPreview,
      previewLimitSeconds: null,
      totalDurationSeconds: null,
      recommendedPlan: null,
      sourceAvailable: true
    };

    // Добавляем в начало первой страницы всех кешей
    // НЕ увеличиваем total - он обновится при следующем fetchHistory с сервера
    newCache.forEach((cachedData, key) => {
      const [page] = key.split(":");
      if (page === "1") {
        // Добавляем в начало списка первой страницы
        newCache.set(key, {
          ...cachedData,
          data: {
            ...cachedData.data,
            items: [optimisticTranscription, ...cachedData.data.items]
            // total остается прежним
          }
        });
      }
    });

    set({ cache: newCache });
  },

  signalWsResync: (): void => {
    const state = get();
    const newCache = new Map(state.cache);

    newCache.forEach((cachedData, key) => {
      newCache.set(key, {
        ...cachedData,
        timestamp: 0
      });
    });

    set({ cache: newCache, wsResyncSignal: state.wsResyncSignal + 1 });
  },

  clearCache: (): void => {
    audioWarmers.clear();
    set({ cache: new Map(), error: null, audioUrls: {} });
  },

  setUpgradeWaiting: (previewId, transcriptionId): void => {
    const state = get();
    set({
      upgradingPreviews: {
        ...state.upgradingPreviews,
        [previewId]: { waitingTranscriptionId: transcriptionId }
      }
    });
  },

  clearUpgradeWaiting: (previewId): void => {
    const state = get();
    const rest = { ...state.upgradingPreviews };
    delete rest[previewId];
    set({ upgradingPreviews: rest });
  },

  setAwaitingPaymentPreview: (previewId): void => {
    set({ awaitingPaymentPreviewId: previewId });
  }
}));
